// Step 17 — shared host derivation for cross-subdomain hard redirects.
//
// Kept small and dependency-free so it can be used by Login.jsx,
// AILogin.jsx and RequireAI.jsx without pulling in any React state.
//
// Convention (matches frontend/src/index.js and SellerWizard.jsx):
//   - `ai.<host>`  → AI application (AIApp)
//   - anything else → broker/seller/buyer application (App)
//
// Rules:
//   - Never hard-code qada.io. The single deployed bundle serves
//     multiple host configurations; derive from the current hostname.
//   - If we can't derive a target, return null so callers can skip
//     the redirect gracefully rather than sending users somewhere
//     unexpected.

function _currentHost() {
  try {
    return (typeof window !== "undefined" && window.location.hostname) || "";
  } catch (_e) {
    return "";
  }
}

function _currentProto() {
  try {
    return (typeof window !== "undefined" && window.location.protocol) || "https:";
  } catch (_e) {
    return "https:";
  }
}

// Broker/normal application host derived from current location.
// Production maps to app.<base> (bare <base> serves the Wagtail
// marketing site, not the React bundle). Dev localhost has no
// separate "app." host — the frontend dev server owns the bare host.
//   ai.qada.io      → app.qada.io
//   app.qada.io     → app.qada.io
//   qada.io         → app.qada.io
//   ai.localhost    → localhost
//   localhost       → localhost
export function brokerPortalHost() {
  const h = _currentHost();
  if (!h) return "";
  const base = h.startsWith("ai.") ? h.slice(3)
             : h.startsWith("app.") ? h.slice(4)
             : h;
  if (base === "localhost" || base === "127.0.0.1") return base;
  return `app.${base}`;
}

// AI application host derived from current location.
//   qada.io         → ai.qada.io
//   app.qada.io     → ai.qada.io
//   ai.qada.io      → ai.qada.io
//   localhost       → ai.localhost
export function aiPortalHost() {
  const h = _currentHost();
  if (!h) return "";
  if (h.startsWith("ai.")) return h;
  const base = h.startsWith("app.") ? h.slice(4) : h;
  return `ai.${base}`;
}

// Full absolute URL to the broker/normal application, preserving the
// current port (needed for local dev where ports != 443).
export function brokerPortalUrl(pathname = "/") {
  const host = brokerPortalHost();
  if (!host) return null;
  const port =
    typeof window !== "undefined" && window.location.port
      ? `:${window.location.port}`
      : "";
  const path = pathname && pathname.startsWith("/") ? pathname : `/${pathname || ""}`;
  return `${_currentProto()}//${host}${port}${path}`;
}

// Full absolute URL to the AI application.
export function aiPortalUrl(pathname = "/") {
  const host = aiPortalHost();
  if (!host) return null;
  const port =
    typeof window !== "undefined" && window.location.port
      ? `:${window.location.port}`
      : "";
  const path = pathname && pathname.startsWith("/") ? pathname : `/${pathname || ""}`;
  return `${_currentProto()}//${host}${port}${path}`;
}

// True when the current window is running on the AI subdomain.
export function isOnAiHost() {
  return _currentHost().startsWith("ai.");
}
